import { ArpMedia } from '@arpeggi/shared'
import { format } from 'date-fns'
import { FC, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDebounce } from 'use-debounce'
import { styled } from '../../../lib/stitches'
import { useArpMedias } from '../../../queries/arpMedia'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import Checkbox from '../../core/Checkbox'
import { Box, IconButton, Spacer } from '../../core/Components'
import { Flex } from '../../core/Flex'
import { ChevronDown, ChevronUp } from '../../core/icons'
import Text from '../../core/Text/Text'
import { TableHeader, TableRow, TableText } from '../components/common'
import { ActionsDropdown } from './ActionsDropdown'
import { BulkActions } from './BulkActions'
import { ErrorMessage } from './ErrorMessage'

const StyledInput = styled('input', {
  width: '100%',
  marginBottom: '$16'
})

export const ArpMediaTableHeader: FC<{ bulkSelect?: boolean }> = ({ bulkSelect }) => (
  <TableRow>
    {bulkSelect && <div style={{ width: '40px', flex: 'unset' }}></div>}
    <TableHeader>ARP Media Index</TableHeader>
    <TableHeader>Media Type</TableHeader>
    <TableHeader>Title</TableHeader>
    <TableHeader>User</TableHeader>
    <TableHeader>Minted at</TableHeader>
    <div style={{ width: '30px', flex: 'unset' }}></div>
  </TableRow>
)

export const ArpMediaRow: React.FC<{
  arpMedia: ArpMedia
  isChecked?: boolean
  handleCheck?: (checked: boolean, arpMedia: ArpMedia) => void
}> = ({ arpMedia, isChecked, handleCheck }) => {
  return (
    <TableRow>
      {handleCheck && (
        <Flex
          css={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            flex: 'unset !important'
          }}
        >
          <Checkbox
            checked={isChecked}
            onClick={(e) => {
              e.preventDefault()
              handleCheck(!isChecked, arpMedia)
            }}
          />
        </Flex>
      )}
      <TableText>{arpMedia.id}</TableText>
      <TableText>{arpMedia.type}</TableText>
      <TableText>{arpMedia.metadata.title}</TableText>
      <TableText>{arpMedia.user.displayName}</TableText>
      <div>{format(new Date(arpMedia.mintedAt), 'MM/dd/yyy')}</div>
      <div style={{ width: '30px', flex: 'unset', overflow: 'visible' }}>
        <ActionsDropdown arpMedia={arpMedia} />
      </div>
    </TableRow>
  )
}

const Arp = () => {
  const [search, setSearch] = useState('')
  const [featured, setFeatured] = useState(false)
  const [isBlocklist, setIsBlocklist] = useState(false)
  const [isDefaultSample, setIsDefaultSample] = useState(false)
  const [debouncedValue] = useDebounce(search, 1000)
  const [bulkSelected, setBulkSelected] = useState<ArpMedia[]>([])
  const [showSelected, setShowSelected] = useState(true)
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false)
  const { data, isLoading, isError, fetchNextPage, hasNextPage } = useArpMedias({
    perPage: 50,
    order: 'desc',
    search: debouncedValue,
    featured: featured === true ? true : undefined,
    isBlocklist: isBlocklist === true ? true : undefined,
    isDefaultSample: isDefaultSample === true ? true : undefined
  })

  const handleBulkSelect = (checked: boolean, arpMedia: ArpMedia) => {
    if (checked) {
      setBulkSelected((prev) => [...prev, arpMedia])
    } else {
      setBulkSelected((prev) => prev.filter((x) => x !== arpMedia))
    }
  }

  const results = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.map((arp) => arp))
  }, [data?.pages])

  return (
    <>
      <Flex css={{ marginBottom: '$16', alignItems: 'center' }}>
        <Text size="24">Arp Media</Text>
        <Spacer />
        <BulkActions
          selectedArpMedia={bulkSelected}
          onClearSelection={() => setBulkSelected([])}
          isDisabled={bulkSelected.length === 0 || isBulkActionLoading}
          onBulkActionStart={() => setIsBulkActionLoading(true)}
          onBulkActionEnd={() => {
            setIsBulkActionLoading(false)
            setBulkSelected([])
          }}
        />
      </Flex>
      <StyledInput
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by title, tags, genre, arp ID, wallet address or display name"
      />
      <Flex css={{ gap: '16px', marginBottom: '24px' }}>
        <Flex css={{ alignItems: 'center', gap: '8px' }}>
          <label>Filter block listed media</label>
          <Checkbox
            checked={isBlocklist}
            onCheckedChange={(value: boolean) => setIsBlocklist(value)}
          />
        </Flex>
        <Flex css={{ alignItems: 'center', gap: '8px' }}>
          <label>Filter featured media</label>
          <Checkbox checked={featured} onCheckedChange={(value: boolean) => setFeatured(value)} />
        </Flex>
        <Flex css={{ alignItems: 'center', gap: '8px' }}>
          <label>Filter default samples</label>
          <Checkbox
            checked={isDefaultSample}
            onCheckedChange={(value: boolean) => setIsDefaultSample(value)}
          />
        </Flex>
      </Flex>

      {bulkSelected.length > 0 && (
        <Box css={{ marginBottom: '$24' }}>
          <Flex>
            <Text size="16">Selected:</Text>
            <Spacer />
            <IconButton
              css={{ width: 'auto', gap: '4px', paddingLeft: '8px', paddingRight: '8px' }}
              onClick={() => setShowSelected((prev) => !prev)}
            >
              <Text>{showSelected ? 'Hide' : 'Show'}</Text>
              {showSelected ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
          </Flex>
          <Box css={{ display: showSelected ? 'initial' : 'none' }}>
            {bulkSelected.map((arpMedia) => (
              <ArpMediaRow
                key={arpMedia.id}
                arpMedia={arpMedia}
                isChecked={Boolean(bulkSelected.find((x) => x.id === arpMedia.id))}
                handleCheck={handleBulkSelect}
              />
            ))}
          </Box>
        </Box>
      )}

      {isError ? (
        <ErrorMessage errorMessage="Something went wrong!" />
      ) : (
        <>
          {isLoading ? (
            <CenteredLoadingAnimation />
          ) : (
            <>
              <ArpMediaTableHeader bulkSelect />
              {data.pages[0].meta.total === 0 && (
                <Text size="16" css={{ marginTop: '$16' }}>
                  No results
                </Text>
              )}
              <InfiniteScroll
                loader={<CenteredLoadingAnimation />}
                dataLength={data?.pages.length * 50}
                next={fetchNextPage}
                hasMore={hasNextPage}
                style={{ overflow: 'visible' }}
              >
                {results.map((arpMedia) => (
                  <ArpMediaRow
                    key={arpMedia.id}
                    arpMedia={arpMedia}
                    isChecked={Boolean(bulkSelected.find((x) => x.id === arpMedia.id))}
                    handleCheck={handleBulkSelect}
                  />
                ))}
              </InfiniteScroll>
            </>
          )}
        </>
      )}
    </>
  )
}

export default Arp
